import * as React from "react";
import { useContext } from "react";
import { Checkbox } from "semantic-ui-react";
import { useFormContext, useWatch } from "react-hook-form";
import { AlertTriangle } from "lucide-react";
import { PolymorphicPlayerCard, PlayerCardReview } from "@/services/bedrock";
import { Alert } from "@/components/ui/alert";
import PlayerCardField from "./Fields/PlayerCardField";
import { PlayerCardSettingsContext } from "../../../Contexts";
import ReviewDisplayTypePicker from "../../../ReviewDisplayTypePicker";
import RecordSelector from "../../../RecordSelector";
import ReviewSourcesSelect from "../../../ReviewSourcesSelect";
import { getReviewWidgetHiddenMessage, MIN_REVIEW_RESPONSES, REVIEW_RATING_THRESHOLDS } from "../../../utils";
import { buildReviewWidgetContent } from "../../../reviewWidgetPreview";
import { useReputationManagementAvailable } from "../../../useReputationManagementAvailable";

interface ReviewWidgetSettingsProps {
  index: number;
}

const ReviewWidgetSettings: React.FC<ReviewWidgetSettingsProps> = ({ index }) => {
  const { User, Reviews } = useContext(PlayerCardSettingsContext);
  const { control, getValues } = useFormContext<PolymorphicPlayerCard>();
  const owner = `widgets.${index}.owner` as const;
  const review = useWatch({ control, name: owner }) as PlayerCardReview;
  const displayType = review.displayType;
  const isTemplate = getValues("isTemplate");
  const playerId = isTemplate ? undefined : getValues("playerId");
  const ratingThreshold = REVIEW_RATING_THRESHOLDS[displayType];

  const reputationManagementAvailable = useReputationManagementAvailable(playerId);
  const hidden = !isTemplate && reputationManagementAvailable && buildReviewWidgetContent(review, Reviews) === null;
  const hiddenMessage = hidden ? getReviewWidgetHiddenMessage(review) : null;

  return (
    <div className="playerCardWidgetOwner playerCardReview">
      {!isTemplate && reputationManagementAvailable === false && (
        <Alert variant="warning">
          <AlertTriangle />
          <Alert.Description>
            {User.isPlayer
              ? "Reviews aren't turned on for your account yet, so this widget won't show on your card. Contact your admin to get access."
              : "Reputation management is not enabled for this Player, so this widget will not be displayed."}
          </Alert.Description>
        </Alert>
      )}
      {hiddenMessage && (
        <Alert variant="warning">
          <AlertTriangle />
          <Alert.Description>{hiddenMessage}</Alert.Description>
        </Alert>
      )}
      <PlayerCardField
        index={index}
        fieldName="displayType"
        fieldLabel="Display"
        rules={{ required: "This is required." }}
        component={ReviewDisplayTypePicker}
        excludedTypes={isTemplate ? ["SPECIFIC_REVIEW"] : []}
      />
      {displayType === "SPECIFIC_REVIEW" && (
        <PlayerCardField
          index={index}
          fieldName="reviewId"
          fieldLabel="Review"
          rules={{ required: "This is required." }}
          component={RecordSelector}
          context={Reviews}
        />
      )}
      {displayType === "FEATURED_REVIEWS" && (
        <>
          <PlayerCardField
            index={index}
            fieldName="sourceSystems"
            fieldLabel="Review sources"
            component={ReviewSourcesSelect}
          />
          <PlayerCardField
            index={index}
            fieldName="autoScrollEnabled"
            fieldLabel="Auto scroll through reviews"
            component={Checkbox}
            toggle
          />
        </>
      )}
      {(displayType === "FEATURED_REVIEWS" || displayType === "SPECIFIC_REVIEW") && (
        <PlayerCardField
          index={index}
          fieldName="useCondensedQuotes"
          fieldLabel="Show condensed quote"
          component={Checkbox}
          toggle
        />
      )}
      {ratingThreshold && (
        <p className="-mt-2 mb-3 text-xs text-muted-foreground">
          Hidden when the rating is below {ratingThreshold.minimum} or there are fewer than {MIN_REVIEW_RESPONSES}{" "}
          {ratingThreshold.responses}.
        </p>
      )}
    </div>
  );
};
export default ReviewWidgetSettings;
